import { SectionEmptyState } from './ui/States';
import { CloseIcon, DeleteIcon, RestoreIcon } from './ui/icons';
import { surfaceShadows } from '../surfaceStyles';
import { displayKeywords } from '../domain/keywords';
import { DialogContent } from './ui/DialogContent';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { userProfileResolverService } from '../services/userProfileResolverService';
import {
  Box,
  Typography,
  List,
  ListItem,
  Button,
  IconButton,
} from '@mui/material';
import { Item } from '../domain/item';
import { ItemListCardContent, itemListCardStyles } from './ItemListCardContent';
import { MAX_DIRECT_BULK_ITEMS } from '../services/bulkOperationService';
import { ResponsiveDialog } from './ui/ResponsiveDialog';
import { DialogHeader } from './ui/DialogHeader';
import { DialogActions } from './ui/DialogActions';
import { usePhoneLayout } from '../hooks/usePhoneLayout';
import { formatDateTime } from '../utils/dateFormat';

interface BinModalProps {
  embedded?: boolean;
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  deletedItems: Item[];
  onRestore: (item: Item) => boolean | void | Promise<boolean | void>;
  onRestoreItems?: (items: Item[]) => Promise<string[]>;
  onDeleteItems?: (items: Item[]) => Promise<string[]>;
  onEmptyBin: () => void | Promise<unknown>;
  readOnly?: boolean;
  canRestore?: boolean;
  canEmptyBin?: boolean;
  collectionId?: string;
  membershipVersion?: number;
  currentUserId?: string;
  currentUsername?: string;
}

function BinItemCardContent({
  item,
  name,
  collectionId,
  membershipVersion,
  currentUserId,
  currentUsername,
  readOnly,
  busy,
  selecting,
  activeItem,
  selected,
  onActivate,
  onToggle,
  trailing,
}: {
  item: Item;
  name: string;
  collectionId?: string;
  membershipVersion?: number;
  currentUserId?: string;
  currentUsername?: string;
  readOnly: boolean;
  busy: boolean;
  selecting: boolean;
  activeItem: string | null;
  selected: boolean;
  onActivate: (item: Item) => void;
  onToggle: (id: string) => void;
  trailing?: React.ReactNode;
}) {
  const [editorUsername, setEditorUsername] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const editorId = item.lastEditedBy;
    if (!editorId) {
      setEditorUsername(null);
      return;
    }
    if (currentUserId && editorId === currentUserId && currentUsername) {
      setEditorUsername(currentUsername);
      return;
    }
    const resolvedCollectionId = item.collectionId || collectionId;
    if (!resolvedCollectionId || membershipVersion === undefined) {
      setEditorUsername(null);
      return;
    }

    void userProfileResolverService
      .resolveUsername(resolvedCollectionId, editorId, membershipVersion)
      .then((resolved) => {
        if (active) setEditorUsername(resolved);
      });

    return () => {
      active = false;
    };
  }, [
    item.lastEditedBy,
    item.collectionId,
    collectionId,
    membershipVersion,
    currentUserId,
    currentUsername,
  ]);

  const deletedTimestamp = item.lastEditedAt || item.createdAt;
  const formattedDate = deletedTimestamp
    ? formatDateTime(deletedTimestamp)
    : '';

  return (
    <ItemListCardContent
      item={item}
      title={name}
      metadata={
        <Box component="span" sx={{ display: 'grid', gap: 0.25 }}>
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.3 }}
          >
            {`Location: ${item.location} | Quantity: ${item.qty}`}
          </Typography>
          {formattedDate && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.3 }}
            >
              {`Deleted ${formattedDate}${editorUsername ? ` by @${editorUsername}` : ''}`}
            </Typography>
          )}
        </Box>
      }
      height={108}
      imageWidth={104}
      imageAlt={name}
      actionProps={{
        disableRipple: true,
        disabled: readOnly || busy,
        'aria-label': selecting ? `Select ${name}` : `Actions for ${name}`,
        'aria-expanded': selecting ? undefined : activeItem === item.id,
        'aria-pressed': selecting ? selected : undefined,
        onClick: (event) => {
          event.stopPropagation();
          onActivate(item);
        },
      }}
      checkbox={
        selecting
          ? {
              checked: selected,
              label: `Select ${name}`,
              disabled: busy,
              onToggle: () => onToggle(item.id),
            }
          : undefined
      }
      trailing={trailing}
    />
  );
}

const BinModal: React.FC<BinModalProps> = ({
  open,
  embedded = false,
  onClose,
  onBack,
  deletedItems,
  onRestore,
  onRestoreItems,
  onDeleteItems,
  onEmptyBin,
  readOnly = false,
  canRestore = !readOnly,
  canEmptyBin = !readOnly,
  collectionId,
  membershipVersion,
  currentUserId,
  currentUsername,
}) => {
  const phone = usePhoneLayout();
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const [swipe, setSwipe] = useState<{ id: string; dx: number } | null>(null);
  const touch = useRef<{ id: string; x: number; y: number } | null>(null);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const suppressClick = useRef<string | null>(null);
  const cancelPress = useCallback(() => {
    clearTimeout(pressTimer.current);
    pressTimer.current = undefined;
  }, []);
  const resetGesture = useCallback(() => {
    cancelPress();
    touch.current = null;
    setSwipe(null);
  }, [cancelPress]);
  const restoreAllowed = !readOnly && canRestore;
  const deleteAllowed = !readOnly && canEmptyBin;
  const canSelect =
    (restoreAllowed && !!onRestoreItems) || (deleteAllowed && !!onDeleteItems);
  const sortedDeletedItems = useMemo(
    () =>
      [...deletedItems].sort(
        (left, right) =>
          Date.parse(right.lastEditedAt || right.createdAt) -
          Date.parse(left.lastEditedAt || left.createdAt),
      ),
    [deletedItems],
  );
  const selectedItems = sortedDeletedItems.filter((item) =>
    selected.includes(item.id),
  );
  const tooMany = selectedItems.length > MAX_DIRECT_BULK_ITEMS;

  useEffect(() => {
    if (!open || !canSelect) {
      setSelecting(false);
      setSelected([]);
    } else {
      setSelected((ids) =>
        ids.filter((id) => deletedItems.some((item) => item.id === id)),
      );
    }
    setActiveItem((id) =>
      open && deletedItems.some((item) => item.id === id) ? id : null,
    );
    resetGesture();
    return cancelPress;
  }, [
    open,
    deletedItems,
    canSelect,
    restoreAllowed,
    deleteAllowed,
    cancelPress,
    resetGesture,
  ]);

  const toggle = (id: string) => {
    if (!running.current)
      setSelected((ids) =>
        ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id],
      );
  };
  const activate = (item: Item) => {
    if (suppressClick.current === item.id) {
      suppressClick.current = null;
      return;
    }
    if (running.current) return;
    if (selecting) toggle(item.id);
    else if (restoreAllowed || (deleteAllowed && onDeleteItems))
      setActiveItem((id) => (id === item.id ? null : item.id));
  };
  const toggleSelectionMode = () => {
    setSelecting(!selecting);
    setSelected([]);
    setActiveItem(null);
    resetGesture();
  };
  const run = async (action: 'restore' | 'delete' | 'empty', items: Item[]) => {
    if (
      running.current ||
      (action === 'restore' ? !restoreAllowed : !deleteAllowed)
    )
      return;
    running.current = true;
    setBusy(true);
    resetGesture();
    try {
      let completed: string[] = [];
      if (action === 'empty') await onEmptyBin();
      else if (action === 'delete')
        completed = (await onDeleteItems?.(items)) || [];
      else if (onRestoreItems) completed = await onRestoreItems(items);
      else if (items.length === 1 && (await onRestore(items[0])) !== false)
        completed = [items[0].id];
      setSelected((ids) => ids.filter((id) => !completed.includes(id)));
      setActiveItem((id) => (id && completed.includes(id) ? null : id));
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  const beginPress = (
    item: Item,
    target: EventTarget,
    x: number,
    y: number,
  ) => {
    cancelPress();
    suppressClick.current = null;
    if (
      running.current ||
      selecting ||
      (target instanceof Element && target.closest('button,input'))
    )
      return;
    touch.current = { id: item.id, x, y };
    if (canSelect)
      pressTimer.current = setTimeout(() => {
        suppressClick.current = item.id;
        resetGesture();
        setActiveItem(null);
        setSelecting(true);
        setSelected([item.id]);
      }, 500);
  };

  const moveSwipe = (x: number, y: number) => {
    const start = touch.current;
    if (!start) return;
    const dx = x - start.x;
    const dy = y - start.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      cancelPress();
      suppressClick.current = start.id;
    }
    if (Math.abs(dy) > 24) {
      resetGesture();
      return;
    }
    setSwipe(
      Math.abs(dx) > 10 &&
        (dx > 0 ? restoreAllowed : deleteAllowed && !!onDeleteItems)
        ? { id: start.id, dx: Math.max(-160, Math.min(160, dx)) }
        : null,
    );
  };
  const endSwipe = (item: Item, x: number, y: number) => {
    const start = touch.current;
    resetGesture();
    if (!start || start.id !== item.id || selecting || running.current) return;
    const dx = x - start.x;
    if (Math.abs(dx) < 80 || Math.abs(y - start.y) > 24) return;
    const action = dx > 0 ? 'restore' : 'delete';
    if (
      action === 'restore' ? restoreAllowed : deleteAllowed && !!onDeleteItems
    ) {
      suppressClick.current = item.id;
      void run(action, [item]);
    }
  };

  return (
    <ResponsiveDialog
      embedded={embedded}
      open={open}
      onClose={onClose}
      aria-labelledby="bin-title"
    >
      <DialogHeader
        id="bin-title"
        onClose={onClose}
        onBack={onBack}
        title="Bin"
        gap={2}
      />
      {deletedItems.length > 0 && canSelect && (
        <Box
          aria-label="Bin selection controls"
          role="group"
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            flexShrink: 0,
            bgcolor: 'background.paper',
            px: phone ? 2 : 3,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            zIndex: 2,
          }}
        >
          {selecting ? (
            <IconButton
              disabled={busy}
              aria-label="Exit selection"
              title="Exit selection"
              sx={{ width: 44, height: 44 }}
              onClick={toggleSelectionMode}
            >
              <CloseIcon />
            </IconButton>
          ) : (
            <Button
              variant="outlined"
              disabled={busy}
              sx={{ ml: 'auto' }}
              onClick={toggleSelectionMode}
            >
              Select
            </Button>
          )}
          {selecting && (
            <>
              <Typography aria-live="polite" sx={{ flex: 1 }}>
                {selectedItems.length} selected
              </Typography>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => setSelected(deletedItems.map((item) => item.id))}
              >
                Select all
              </Button>
              {selectedItems.length > 0 && (
                <Button
                  variant="outlined"
                  disabled={busy}
                  onClick={() => setSelected([])}
                >
                  Clear
                </Button>
              )}
            </>
          )}
        </Box>
      )}
      <DialogContent>
        {readOnly && <Typography>(View only)</Typography>}
        {!readOnly && !canRestore && (
          <Typography>(Restore unavailable during transfer)</Typography>
        )}
        {tooMany && (
          <Typography color="error">
            Select at most {MAX_DIRECT_BULK_ITEMS} items per action.
          </Typography>
        )}
        {sortedDeletedItems.length ? (
          <List>
            {sortedDeletedItems.map((item) => {
              const name = displayKeywords(item.keywords);
              const dx = swipe?.id === item.id ? swipe.dx : 0;
              return (
                <Box
                  component="li"
                  role="presentation"
                  key={item.id}
                  sx={{
                    listStyle: 'none',
                    mb: 1.5,
                    boxShadow: selected.includes(item.id)
                      ? 'none'
                      : surfaceShadows.raised,
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative',
                    bgcolor: dx
                      ? dx > 0
                        ? 'success.main'
                        : 'error.main'
                      : 'background.paper',
                  }}
                >
                  {!!dx && (
                    <Box
                      aria-hidden="true"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        justifyContent: dx > 0 ? 'flex-start' : 'flex-end',
                        color: 'common.white',
                      }}
                    >
                      {dx > 0 ? <RestoreIcon /> : <DeleteIcon />}
                      <Typography sx={{ mx: 1 }}>
                        {dx > 0 ? 'Restore' : 'Delete'}
                      </Typography>
                    </Box>
                  )}
                  <ListItem
                    component="div"
                    role="listitem"
                    disablePadding
                    onClick={() => activate(item)}
                    onContextMenu={(event) => {
                      if (canSelect) event.preventDefault();
                    }}
                    onMouseDown={(event) => {
                      if (event.button === 0)
                        beginPress(
                          item,
                          event.target,
                          event.clientX,
                          event.clientY,
                        );
                    }}
                    onMouseMove={(event) =>
                      moveSwipe(event.clientX, event.clientY)
                    }
                    onMouseUp={(event) => {
                      if (event.button === 0)
                        endSwipe(item, event.clientX, event.clientY);
                    }}
                    onMouseLeave={resetGesture}
                    onDragStart={(event) => event.preventDefault()}
                    onTouchStart={(event) => {
                      touch.current = null;
                      cancelPress();
                      if (event.touches.length !== 1) return;
                      const point = event.touches[0];
                      beginPress(
                        item,
                        event.target,
                        point.clientX,
                        point.clientY,
                      );
                    }}
                    onTouchMove={(event) => {
                      if (event.touches.length !== 1) {
                        resetGesture();
                        return;
                      }
                      moveSwipe(
                        event.touches[0].clientX,
                        event.touches[0].clientY,
                      );
                    }}
                    onTouchCancel={resetGesture}
                    onTouchEnd={(event) => {
                      const point = event.changedTouches[0];
                      if (point) endSwipe(item, point.clientX, point.clientY);
                      else {
                        resetGesture();
                      }
                      if (suppressClick.current === item.id)
                        event.preventDefault();
                    }}
                    sx={{
                      ...itemListCardStyles,
                      boxShadow: 'none',
                      position: 'relative',
                      '&:hover, &:active, &:has(.Mui-focusVisible)':
                        !readOnly && !busy
                          ? {
                              backgroundImage: (theme) =>
                                `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
                            }
                          : undefined,
                      '& > .MuiListItemButton-root:hover, & > .MuiListItemButton-root.Mui-focusVisible':
                        {
                          bgcolor: 'transparent',
                        },
                      '&::after': selected.includes(item.id)
                        ? {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            borderRadius: 'inherit',
                            boxShadow: surfaceShadows.inset,
                            pointerEvents: 'none',
                            zIndex: 1,
                          }
                        : undefined,
                      transform: `translateX(${dx}px)`,
                      transition: dx ? 'none' : 'transform 150ms ease-out',
                      touchAction: 'pan-y',
                      userSelect:
                        restoreAllowed || deleteAllowed || canSelect
                          ? 'none'
                          : undefined,
                      WebkitTouchCallout: canSelect ? 'none' : undefined,
                      bgcolor: selected.includes(item.id)
                        ? 'action.selected'
                        : 'background.paper',
                    }}
                  >
                    <BinItemCardContent
                      item={item}
                      name={name}
                      collectionId={collectionId}
                      membershipVersion={membershipVersion}
                      currentUserId={currentUserId}
                      currentUsername={currentUsername}
                      readOnly={readOnly}
                      busy={busy}
                      selecting={selecting}
                      activeItem={activeItem}
                      selected={selected.includes(item.id)}
                      onActivate={activate}
                      onToggle={toggle}
                      trailing={
                        !selecting &&
                        activeItem === item.id && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              flexShrink: 0,
                              mr: 1,
                            }}
                          >
                            {restoreAllowed && (
                              <IconButton
                                disabled={busy}
                                aria-label={`Restore ${name}`}
                                title="Restore"
                                sx={{ width: 44, height: 44 }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void run('restore', [item]);
                                }}
                              >
                                <RestoreIcon />
                              </IconButton>
                            )}
                            {deleteAllowed && onDeleteItems && (
                              <IconButton
                                disabled={busy}
                                color="error"
                                aria-label={`Delete ${name} permanently`}
                                title="Delete permanently"
                                sx={{ width: 44, height: 44 }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void run('delete', [item]);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        )
                      }
                    />
                  </ListItem>
                </Box>
              );
            })}
          </List>
        ) : (
          <SectionEmptyState icon={DeleteIcon} message="Bin is empty" />
        )}
      </DialogContent>
      {deletedItems.length > 0 && (selecting || deleteAllowed) && (
        <DialogActions compact={selecting}>
          {selecting ? (
            <>
              {deleteAllowed && onDeleteItems && (
                <Button
                  variant="contained"
                  color="error"
                  sx={{ flex: 1, minWidth: 0 }}
                  disabled={busy || !selectedItems.length || tooMany}
                  onClick={() => void run('delete', selectedItems)}
                >
                  Delete
                </Button>
              )}
              {restoreAllowed && onRestoreItems && (
                <Button
                  variant="contained"
                  sx={{ flex: 1, minWidth: 0 }}
                  disabled={busy || !selectedItems.length || tooMany}
                  onClick={() => void run('restore', selectedItems)}
                >
                  Restore
                </Button>
              )}
            </>
          ) : (
            deleteAllowed && (
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                disabled={busy}
                color="error"
                onClick={() => void run('empty', deletedItems)}
              >
                {deletedItems.length > MAX_DIRECT_BULK_ITEMS
                  ? `Delete first ${MAX_DIRECT_BULK_ITEMS}`
                  : 'Empty Bin'}
              </Button>
            )
          )}
        </DialogActions>
      )}
    </ResponsiveDialog>
  );
};

export default BinModal;
